import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { Select } from '@coursera/coursera-ui';

import _t from 'i18n!nls/staff-management';

import { createSelector } from '../common/memoizer';
import type {
  EmailableInterface,
  StaffManagementStoreV2 as StaffManagementStoreClass,
  StaffMembershipType,
  StaffMembershipUserInterface,
} from '../store';
import { actions, selectors } from '../store';
import withFluxibleContextAsProps from '../withFluxibleContextAsProps';

type Props = {
  filterOptions?(options: Array<EmailableInterface>, searchText: string): void;
  onChange?(value: Array<EmailableInterface>): void;
  options?: Array<StaffMembershipUserInterface>;
  value?: Array<EmailableInterface>;
};

export const SelectEmail = ({ filterOptions, onChange = _.noop, options, value }: Props) => {
  return (
    <div className="pii-hide fs-exclude" data-e2e={SelectEmail.e2eName}>
      <Select
        multi={true}
        searchable={true}
        creatable={true}
        labelKey="displayName"
        valueKey="email"
        value={value}
        options={options}
        filterOptions={filterOptions}
        onChange={onChange}
        placeholder={_t('Enter email address(es)')}
        optionHeight={36}
      />
    </div>
  );
};

SelectEmail.e2eName = 'staff-management-select-email';

export const filterOptions = (
  options: Array<StaffMembershipUserInterface>,
  searchText: string
): Array<EmailableInterface> => {
  if (!searchText) {
    return options;
  }
  return _.filter(options, (staff) => {
    const fullName = staff.fullName ? staff.fullName.toLowerCase() : '';
    const email = staff.email ? staff.email.toLowerCase() : '';
    return fullName.indexOf(searchText.toLowerCase()) >= 0 || email.indexOf(searchText.toLowerCase()) >= 0;
  });
};

export const onChange = (executeAction: $TSFixMe) => (values: Array<EmailableInterface>) => {
  executeAction(
    actions.addFlow.users,
    _.map(values, (value) => ({
      ...value,
      email: _.trim(value.email),
    }))
  );
};

// We only allow users who do not already have memberships within scope to be
// an option in the auto-complete.
export const optionsNotYetStaff = createSelector(
  selectors.staffMemberships.getMembershipsByUserIdLimitedByType,
  selectors.staffMemberships.getUsersById,
  (
    membershipsByUserIdLimitedByType: { [id: string]: Array<StaffMembershipType> },
    usersById: { [id: string]: StaffMembershipUserInterface }
  ) => {
    const eligibleUsers = _.filter(usersById, (user) => _.isEmpty(membershipsByUserIdLimitedByType[user.id]));
    return _.map(eligibleUsers, (user) => ({
      ...user,
      displayName: `${user.fullName} <${user.email}>`,
    }));
  }
);

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  return {
    filterOptions,
    options: optionsNotYetStaff(state),
    onChange: onChange(executeAction),
    value: state.ui?.addFlow?.users,
  };
};

export default compose<Props, Props>(
  withFluxibleContextAsProps(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector)
)(SelectEmail);
